import React, { useState } from 'react';

export default function InvestmentGoalsStep({ onNext, onBack, data }) {
  const [selectedGoal, setSelectedGoal] = useState(data.goal || '');

  const goals = [
    {
      id: 'long-term',
      title: 'Long-term growth',
      description: 'Build wealth over time with strategic investments',
      color: '#10b981', // Green
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
        </svg>
      )
    },
    {
      id: 'short-term',
      title: 'Short-term trading',
      description: 'Active trading for quick profits and market opportunities',
      color: '#f59e0b', // Orange
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    },
    {
      id: 'passive',
      title: 'Passive investing',
      description: 'Set it and forget it with automated strategies',
      color: '#8b5cf6', // Purple
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    },
    {
      id: 'learning',
      title: 'Research and learning',
      description: 'Understand markets and develop investment knowledge',
      color: '#06b6d4', // Light Blue
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    }
  ];

  const handleNext = () => {
    if (selectedGoal) {
      onNext({ goal: selectedGoal });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3Cpattern id='grid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='rgba(255,255,255,0.1)' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="flex-1 flex flex-col justify-start items-center px-4 pt-4 relative z-10">
        {/* Logo and Brand */}
        <div className="text-center mb-6 px-2 mt-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
            <span className="text-white font-bold text-lg">CI</span>
          </div>
          <h1 className="text-white text-lg font-semibold">CryptoIndicators.AI</h1>
        </div>

        {/* Main Question */}
        <div className="text-center mb-6 max-w-md px-2">
          <h2 className="text-2xl font-bold text-white mb-2">
            What's your main goal with crypto?
          </h2>
        </div>

        {/* Goal Options */}
        <div className="w-full max-w-md space-y-3 mb-6 px-2">
          {goals.map((goal) => (
            <button
              key={goal.id}
              onClick={() => setSelectedGoal(goal.id)}
              className={`w-full p-4 rounded-xl border-2 transition-all duration-300 ${
                selectedGoal === goal.id
                  ? 'bg-gray-800 border-blue-500 shadow-lg shadow-blue-500/20'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800/70'
              }`}
            >
              <div className="flex items-center space-x-4">
                {/* Colored Icon Circle */}
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 ${
                    selectedGoal === goal.id ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-900' : ''
                  }`}
                  style={{ backgroundColor: goal.color }}
                >
                  {goal.icon}
                </div>
                
                {/* Text Content */}
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-white text-lg mb-1">
                    {goal.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {goal.description}
                  </p>
                </div>

                {/* Selection Indicator */}
                {selectedGoal === goal.id && (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Next Button */}
        <div className="w-full max-w-md px-2">
          <button
            onClick={handleNext}
            disabled={!selectedGoal}
            className="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
} 